import React, { useEffect, useState, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { Box, Inline, Text } from '@atlaskit/primitives';

import { GeneratingStreamAnimation } from '@atlassian/ai-components';

import { useAudioPlaybackState } from '../../hooks/useAudioPlaybackState';

type AudioTimeIndicatorProps = {
	audioRef: React.RefObject<HTMLAudioElement>;
};

const i18n = defineMessages({
	generatingAudioAnimationAlt: {
		id: 'audio.audio-time-indicator.generating-alt',
		defaultMessage: 'Generating',
		description: 'Alt text of the generation animation',
	},
});

const formatTime = (timeInSeconds: number): string => {
	const minutes = Math.floor(timeInSeconds / 60);
	const seconds = Math.floor(timeInSeconds % 60);
	return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

export const AudioTimeIndicator = ({ audioRef }: AudioTimeIndicatorProps) => {
	const { formatMessage } = useIntl();
	const [{ isPlaying }] = useAudioPlaybackState();
	const timeUpdateInterval = useRef<ReturnType<typeof setInterval> | null>(null);

	const [currentTimeDisplay, setCurrentTimeDisplay] = useState(
		formatTime(audioRef.current?.currentTime ?? 0),
	);
	const [totalTimeDisplay, setTotalTimeDisplay] = useState(
		formatTime(audioRef.current?.duration ?? 0),
	);

	const isAudioDurationUnknown =
		!audioRef.current?.duration || audioRef.current?.duration === Infinity;

	const cleanupInterval = () => {
		if (timeUpdateInterval.current) {
			clearInterval(timeUpdateInterval.current);
		}
		timeUpdateInterval.current = null;
	};

	useEffect(() => {
		if (!timeUpdateInterval.current) {
			if (isPlaying) {
				timeUpdateInterval.current = setInterval(() => {
					setCurrentTimeDisplay(formatTime(audioRef.current?.currentTime ?? 0));

					if (!isAudioDurationUnknown) {
						setTotalTimeDisplay(formatTime(audioRef.current?.duration));
					}

					if (!isPlaying) {
						cleanupInterval();
					}
				}, 1000);
			} else {
				cleanupInterval();
			}

			return () => {
				cleanupInterval();
			};
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentTimeDisplay, isPlaying, audioRef]);

	return (
		<Box testId="audio-time-indicator">
			<Inline alignInline="center" space="space.100">
				<Text color="color.text.subtle">{currentTimeDisplay}</Text>
				<Text color="color.text.subtlest">/</Text>

				{isAudioDurationUnknown || totalTimeDisplay === '0:00' ? (
					<GeneratingStreamAnimation alt={formatMessage(i18n.generatingAudioAnimationAlt)} />
				) : (
					// Extra padding ensures there is no spacing change when transitioning from the generating animation
					<Box paddingBlock="space.025">
						<Text color="color.text.subtle">{totalTimeDisplay}</Text>
					</Box>
				)}
			</Inline>
		</Box>
	);
};
